import { useTranslation } from 'react-i18next';
import { UtilityDrawerHookReturnType } from '../../utility-drawer/utility-drawer.hooks';
import FormDrawer, {
  FormDrawerProps,
} from '../../form-drawer/form-drawer.component';
import SetTemporaryHoursForm from 'components/forms/location/hours/set-temporary-hours-form/set-temporary-hours-form.component';
import { useSetTemporaryHoursForm } from 'components/forms/location/hours/set-temporary-hours-form/set-temporary-hours-form.hooks';
import { useLocationHoursContext } from 'components/location-hours/location-hours.context';
import { SetHoursFormDataProps } from 'components/location-hours/location-hours.utils';
import SetTemporaryHoursFormFooter from 'components/forms/location/hours/set-temporary-hours-form/set-temporary-hours-form-footer/set-temporary-hours-form-footer.component';
import { LoadingButton } from '@mui/lab';
import { Button, Stack } from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';

type SetTemporaryHoursDrawerProps = {
  drawerData: SetHoursFormDataProps;
} & Omit<FormDrawerProps, 'children' | 'onClose' | 'formMethods' | 'onValid'> &
  UtilityDrawerHookReturnType;

const SetTemporaryHoursDrawer = ({
  title,
  open,
  close,
  drawerData,
  ...rest
}: SetTemporaryHoursDrawerProps) => {
  const { t } = useTranslation();
  const {
    temporaryHours: { removeDummyEvent, addEvents, temporaryHoursRef },
  } = useLocationHoursContext();
  const { form } = useSetTemporaryHoursForm(drawerData);

  const finalOnValid = data => {
    try {
      removeDummyEvent();
      addEvents(data);

      form.reset();
      close();
    } catch (_e) {}
  };

  const cancel = () => {
    removeDummyEvent();

    form.reset();
    form.clearErrors();
    close();
  };

  return (
    <FormDrawer
      title={t('drawer.setTemporaryHoursDrawer.title')}
      data-testid="SetTemporaryHoursDrawer"
      open={open}
      onValid={finalOnValid}
      onClose={cancel}
      cancel={cancel}
      formMethods={form}
      footerContent={<SetTemporaryHoursFormFooter />}
      buttons={(isSubmitting, isValidating) => {
        return (
          <>
            <LoadingButton
              type="submit"
              fullWidth
              loading={isSubmitting || isValidating}
              data-testid="FormDrawer.Button.Submit"
            >
              {t('drawer.setTemporaryHoursDrawer.buttons.apply')}
            </LoadingButton>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                onClick={cancel}
                variant="outlined"
                data-testid="FormDrawer.Button.Cancel"
                disabled={isSubmitting || isValidating}
              >
                {t('forms.buttons.cancel')}
              </Button>
              {drawerData?.eventId && (
                <Button
                  color="error"
                  onClick={() => {
                    temporaryHoursRef.current
                      .getApi()
                      .getEventById(drawerData?.eventId)
                      .remove();
                    close();
                  }}
                  data-testid="FormDrawer.Button.Delete"
                  disabled={isSubmitting || isValidating}
                  sx={{ minWidth: 'auto' }}
                  variant="outlined"
                >
                  <DeleteOutlined />
                </Button>
              )}
            </Stack>
          </>
        );
      }}
      {...rest}
    >
      {open && <SetTemporaryHoursForm eventId={drawerData?.eventId} />}
    </FormDrawer>
  );
};

export default SetTemporaryHoursDrawer;
